import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { FlexProps } from 'UIKit/Box/types'
import Button from 'UIKit/Button'
import Image from 'UIKit/Image'
import CircleLoader from 'UIKit/Loader/CircleLoader'
import Text from 'UIKit/Text'
import { Token } from 'config/types'
import { Game, GameDemoSession } from 'config/types/game'
import useAuthenticationModal from 'hooks/useAuthenticationModal'
import Column from 'layout/Components/Column'
import { RowBetween, RowCenter } from 'layout/Components/Row'
import { forwardRef, useCallback, useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { useClientIpInfo, useProviderInfo, useValidatePlayToken } from 'state/app/hooks'
import { useAuth } from 'state/auth/hooks'
import { useTokenSelected } from 'state/session/hooks'
import styled from 'styled-components'
import colors from 'theme/ui/colors'
import FavoriteGameButton from 'views/Games/components/FavoriteGameButton'
import NotAvailableGameDesktopOverlay from 'views/Games/components/NotAvailableGameDesktopOverlay'
import ShareGameButton from 'views/Games/components/ShareGameButton'
import { useRestrictedGamesC2PBonus } from '../../hooks/useRestrictedGamesC2PBonus'
import BalanceSelectSection from './BalanceSelectSection'

interface GamesInfoProps extends FlexProps {
  game: Game
  handlePlayGame: (isDemo: boolean, selectedToken?: Token) => void
  isLoading?: boolean
  gameDemoSession?: GameDemoSession
  isIngame?: boolean
  scale?: number
}

const GameInfo = forwardRef<HTMLDivElement, GamesInfoProps>(
  ({ game, handlePlayGame, isLoading, gameDemoSession, scale = 1, ...props }, ref) => {
    const { isSigned, hasSession } = useAuth()

    const provider = useProviderInfo(game.providerCode)
    const { country } = useClientIpInfo()
    const playingToken = useTokenSelected()

    const [selectedToken, setSelectedToken] = useState(playingToken)
    const { isBlackList } = useValidatePlayToken(selectedToken)
    const isBlockedCountry = provider?.blacklistCountry?.includes(country)
    const isRestrictedGame = useRestrictedGamesC2PBonus(game.code)

    const [onPresentLoginModal] = useAuthenticationModal()

    const handleUnlockClick = useCallback(() => {
      onPresentLoginModal()
    }, [onPresentLoginModal])

    useEffect(() => {
      setSelectedToken(playingToken)
    }, [playingToken])

    return (
      <Flex alignItems="center" justifyContent="flex-start" flexDirection="column" position="relative" {...props}>
        {isBlockedCountry || isRestrictedGame || game.isMaintenance ? (
          <NotAvailableGameDesktopOverlay isBlocked={isBlockedCountry} isRestricted={isRestrictedGame} />
        ) : (
          <StyledContainer
            background={colors.cardBackground}
            borderRadius="20px"
            width={['100%', '', 'fit-content']}
            style={{
              transform: `scale(${scale})`,
            }}
            ref={ref}
          >
            <StyledGameImage
              borderRadius="8px"
              overflow="hidden"
              mb={['20px', '20px', '20px', '20px', '40px']}
              mx="auto"
              position="relative"
            >
              <Image src={game.thumbnail} width={200} height={200} blurLoader />
            </StyledGameImage>

            {!isSigned && (
              <Flex
                display={['none !important', '', 'flex !important']}
                justifyContent="center"
                mb={['20px', '20px', '20px', '20px', '40px']}
                maxWidth={300}
              >
                <Text fontWeight={600} fontSize={['', '', '16px', '20px', '22px']} ellipsis={1}>
                  {game.name}
                </Text>
              </Flex>
            )}

            <RowBetween
              display={['flex', 'flex', 'none !important']}
              m="12px 0 8px 0"
              alignSelf="flex-start"
              width="100%"
            >
              <Column>
                <Text fontWeight={700} fontSize="14px" ellipsis={2} width={178}>
                  {game.name}
                </Text>
                <Text fontWeight={400} fontSize="10px" color="textTertiary" mt="6px">
                  {provider.name}
                </Text>
              </Column>

              <RowCenter>
                <FavoriteGameButton game={game} size="24px" />
                <ShareGameButton game={game} size="24px" ml="4px" />
              </RowCenter>
            </RowBetween>

            {isSigned && (
              <BalanceSelectSection width="100%" setSelectedToken={setSelectedToken} selectedToken={selectedToken} />
            )}

            <StyledButtonWrapper flexDirection={'row'} justifyContent="center" mt={['16px', '', 20, 20, 40]}>
              {hasSession ? (
                <StyledButton
                  onClick={() => handlePlayGame(false, selectedToken)}
                  disabled={
                    isLoading || isBlackList || !isSigned || isBlockedCountry || isRestrictedGame || game.isMaintenance
                  }
                >
                  {isLoading && (
                    <Box px="4px" mr="4px">
                      <StyledProgress />
                    </Box>
                  )}

                  <Text
                    bold
                    fontSize="14px"
                    fontWeight={600}
                    color={isLoading || isBlackList || !isSigned ? 'textSubtle' : 'text'}
                  >
                    <Trans>Play Now</Trans>
                  </Text>
                </StyledButton>
              ) : (
                <StyledButton onClick={handleUnlockClick}>
                  <Text bold fontSize="14px" fontWeight={600}>
                    <Trans>Login and Play</Trans>
                  </Text>
                </StyledButton>
              )}
              {gameDemoSession && (
                <StyledButtonTryDemo
                  disabled={isBlockedCountry || isRestrictedGame || game.isMaintenance}
                  ml="12px"
                  onClick={() => handlePlayGame(true)}
                >
                  <Text bold fontSize="14px" fontWeight={600}>
                    <Trans>Try Demo</Trans>
                  </Text>
                </StyledButtonTryDemo>
              )}
            </StyledButtonWrapper>
          </StyledContainer>
        )}
      </Flex>
    )
  },
)

const StyledButton = styled(Button).attrs({ variant: 'primary' })``
const StyledButtonTryDemo = styled(Button)`
  background: rgba(255, 255, 255, 0.12) !important;
`

const StyledProgress = styled(CircleLoader)`
  color: #fff !important;
  margin-right: 4px;
`

const StyledGameImage = styled(Box)``

const StyledContainer = styled(Box)`
  padding: 24px;
  min-width: 350px;
  margin-top: 40px;

  ${StyledGameImage} {
    width: 130px;
    height: 130px;
    margin-bottom: 24px;
  }

  @media screen and (min-height: 760px) {
    padding: 24px;
    margin-top: 36px;
    min-width: 350px;

    ${StyledGameImage} {
      width: 150px;
      height: 150px;
      margin-bottom: 24px;
    }
  }

  @media screen and (min-height: 860px) {
    padding: 40px;
    margin-top: 64px;
    min-width: 408px;

    ${StyledGameImage} {
      width: 200px;
      height: 200px;
      margin-bottom: 40px;
    }
  }
`

const StyledButtonWrapper = styled(Flex)`
  ${StyledButton}, ${StyledButtonTryDemo} {
    min-width: 148px !important;
    width: 100%;

    ${({ theme: { mediaQueries } }) => mediaQueries.sm} {
      min-width: 160px !important;
    }
  }
`

export default GameInfo
